import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import Points from 'components/Points';
import { SummonAdminWizard } from 'components/SummonAdminWizard/SummonAdminWizard';
import { summaryFromTradeContext } from 'components/ContactUserModal/contactTradeSummary';
import { useGetConversationThreadQuery } from 'store/services/message/messageApi';
import type { ConversationThread } from 'types/interfaces/message';
import type { CaseThreadContext, OfferThreadContext, ThreadCaseSummary, TradeThreadContext } from 'types/interfaces/thread';
import { isCaseContext, isOfferContext, isTradeContext } from 'types/interfaces/thread';
import { caseReasonShortLabel } from 'constants/caseReasons';
import { formatCaseNumber } from 'utils/caseNumber';
import { STATUS_TONE_CLASS, THREAD_TYPE_LABELS, THREAD_TYPE_STYLES } from '../threadTypeStyles';
import { caseSteps, offerSteps, threadSubjectType, tradePagePath, tradeSteps } from '../threadSubject';
import { CASE_STATUS_CHIP, OFFER_STATUS_CHIP, TRADE_STATUS_CHIP } from '../threadStatus';
import { LifecycleStepper } from './rail/LifecycleStepper';
import { FactsGrid, formatRailDate } from './rail/FactsGrid';
import { PackageList } from './rail/PackageList';
import { SummonAdminAction } from './SummonAdminAction';
import './ContextRail.css';

const StatusChip: React.FC<{ chip: { label: string; tone: keyof typeof STATUS_TONE_CLASS } | undefined; fallback: string }> = ({ chip, fallback }) =>
  chip ? (
    <span className={`status-badge rail-status ${STATUS_TONE_CLASS[chip.tone]}`}>{chip.label}</span>
  ) : (
    <span className="status-badge rail-status status-closed">{fallback}</span>
  );

interface RailProps {
  thread: ConversationThread;
  /** Opens the Summon Admin wizard for this trade (rail and sheet only). */
  onSummon: (trade: TradeThreadContext) => void;
  /** Mobile sheet: the action buttons render into its pinned footer instead of inline. */
  actionsPortalTarget?: HTMLElement | null;
}

/** Inline on desktop; portalled into the sheet footer on mobile. */
const RailActions: React.FC<{ target?: HTMLElement | null; children: React.ReactNode }> = ({ target, children }) => {
  const node = <div className="rail-actions">{children}</div>;
  return target ? createPortal(node, target) : node;
};

/**
 * The trade's case, above the lifecycle stepper (change 2): a case is a phase
 * of the trade thread, so its facts sit with the trade rather than in a thread
 * of their own. Reported cards are marked in the package list below.
 */
const CaseSection: React.FC<{ summary: ThreadCaseSummary; viewerUsername?: string }> = ({ summary, viewerUsername }) => {
  const number = formatCaseNumber(summary.caseNumber);
  const openedBy = summary.openedBy
    ? `${summary.openedBy.username === viewerUsername ? 'You' : summary.openedBy.username} (${summary.openedBy.role === 'SENDER' ? 'sender' : 'receiver'})`
    : null;
  return (
    <section className="rail-case" data-testid="rail-case-section">
      <header className="rail-header">
        <span className="rail-kicker" style={{ color: THREAD_TYPE_STYLES.CASE.color }}>Case</span>
        <span className="rail-title">{number ?? ''}</span>
        <StatusChip chip={CASE_STATUS_CHIP[summary.status.toUpperCase()]} fallback={summary.status} />
      </header>
      <FactsGrid
        facts={[
          { label: 'Reason', value: caseReasonShortLabel(summary.reason ?? null) },
          { label: 'Opened by', value: openedBy },
          { label: 'Also requested', value: summary.alsoRequestedByUsername },
          { label: 'Due', value: formatRailDate(summary.dueAt) },
        ]}
      />
    </section>
  );
};

interface SubjectRailProps {
  viewerUsername?: string;
  /** The other party on the thread; the Open in Sending/Receiving link searches for them. */
  partnerUsername: string;
}

const TradeRail: React.FC<{ trade: TradeThreadContext } & SubjectRailProps & Pick<RailProps, 'onSummon' | 'actionsPortalTarget'>> = ({ trade, viewerUsername, partnerUsername, onSummon, actionsPortalTarget }) => {
  const navigate = useNavigate();
  const sending = trade.role === 'SENDER';
  return (
    <>
      <header className="rail-header">
        <span className="rail-kicker" style={{ color: THREAD_TYPE_STYLES.TRADE.color }}>Trade</span>
        <span className="rail-title">{trade.tradeNumber}</span>
        <StatusChip chip={TRADE_STATUS_CHIP[trade.status.toUpperCase()]} fallback={trade.status} />
      </header>
      {trade.caseSummary && <CaseSection summary={trade.caseSummary} viewerUsername={viewerUsername} />}
      <LifecycleStepper steps={tradeSteps(trade.status, trade.shippedAt)} />
      <FactsGrid
        facts={[
          { label: 'Trade #', value: trade.tradeNumber },
          { label: sending ? "You're" : "You're", value: sending ? 'Sending' : 'Receiving' },
          { label: 'Shipped', value: formatRailDate(trade.shippedAt) ?? 'Not yet' },
          { label: 'Est. arrival', value: trade.estimatedArrivalLabel },
          { label: 'Tracking', value: trade.trackingNumber },
          { label: 'Point value', value: <Points points={trade.totalValue} type="default" /> },
        ]}
      />
      <PackageList items={trade.items} affectedItemIds={trade.caseSummary?.affectedItemIds} />
      <RailActions target={actionsPortalTarget}>
        <button
          type="button"
          className="rail-link-button"
          onClick={() => navigate(tradePagePath({ role: trade.role, tradeStatus: trade.status, partnerUsername }))}
        >
          {sending ? 'Open in Sending' : 'Open in Receiving'}
        </button>
        <SummonAdminAction
          caseState={trade.caseSummary ? { caseNumber: trade.caseSummary.caseNumber, status: trade.caseSummary.status, conversationId: trade.caseSummary.conversationId } : null}
          onSummon={() => onSummon(trade)}
          inThread
        />
      </RailActions>
    </>
  );
};

const CaseRail: React.FC<{ caseContext: CaseThreadContext } & SubjectRailProps & Pick<RailProps, 'actionsPortalTarget'>> = ({ caseContext, viewerUsername, partnerUsername, actionsPortalTarget }) => {
  const number = formatCaseNumber(caseContext.caseNumber);
  const openedBy = caseContext.summonedByUsername === viewerUsername ? 'You' : caseContext.summonedByUsername;
  const role = caseContext.summonedByRole === 'SENDER' ? 'sender' : 'receiver';
  return (
    <>
      <header className="rail-header">
        <span className="rail-kicker" style={{ color: THREAD_TYPE_STYLES.CASE.color }}>Case</span>
        <span className="rail-title">{number ?? ''}</span>
        <StatusChip chip={CASE_STATUS_CHIP[caseContext.status.toUpperCase()]} fallback={caseContext.status} />
      </header>
      <LifecycleStepper steps={caseSteps(caseContext.status)} />
      <FactsGrid
        facts={[
          { label: 'Reason', value: caseReasonShortLabel(caseContext.reason) },
          { label: 'Opened by', value: `${openedBy} (${role})` },
          { label: 'Also requested', value: caseContext.alsoRequestedByUsername },
          { label: 'Trade #', value: caseContext.tradeNumber },
          { label: 'Due', value: formatRailDate(caseContext.dueDate) },
          { label: 'Point value', value: <Points points={caseContext.totalValue} type="default" /> },
        ]}
      />
      <PackageList items={caseContext.items} affectedItemIds={caseContext.affectedItemIds} />
      <RailActions target={actionsPortalTarget}>
        <Link to={tradePagePath({ role: caseContext.role, tradeStatus: caseContext.tradeStatus, partnerUsername })} className="rail-link-button">
          Open trade {caseContext.tradeNumber}
        </Link>
      </RailActions>
    </>
  );
};

const OfferRail: React.FC<{ offer: OfferThreadContext } & Pick<RailProps, 'actionsPortalTarget'>> = ({ offer, actionsPortalTarget }) => (
  <>
    <header className="rail-header">
      <span className="rail-kicker" style={{ color: THREAD_TYPE_STYLES.OFFER.color }}>Offer</span>
      <span className="rail-title">{offer.counterpartyUsername ? `with ${offer.counterpartyUsername}` : ''}</span>
      <StatusChip chip={OFFER_STATUS_CHIP[offer.status.toUpperCase()]} fallback={offer.status} />
    </header>
    <LifecycleStepper steps={offerSteps(offer)} />
    <FactsGrid
      facts={[
        {
          label: 'You give',
          value: (
            <span className="rail-inline-points">
              {offer.give.count} card{offer.give.count === 1 ? '' : 's'} · <Points points={offer.give.points} type="inline" />
            </span>
          ),
        },
        {
          label: 'You get',
          value: (
            <span className="rail-inline-points">
              {offer.get.count} card{offer.get.count === 1 ? '' : 's'} · <Points points={offer.get.points} type="inline" />
            </span>
          ),
        },
        { label: 'Proposed', value: formatRailDate(offer.proposedAt) },
        { label: 'Resolved', value: formatRailDate(offer.resolvedAt) },
        { label: 'Trades created', value: offer.createdTradeNumbers.length > 0 ? offer.createdTradeNumbers.join(', ') : null },
      ]}
    />
    <RailActions target={actionsPortalTarget}>
      <Link to="/trade-offers" className="rail-link-button">Open Trade Offers</Link>
    </RailActions>
  </>
);

const UnlinkedRail: React.FC<{ kind: 'DIRECT' | 'SYSTEM'; username: string }> = ({ kind, username }) => (
  <>
    <header className="rail-header">
      <span className="rail-kicker" style={{ color: THREAD_TYPE_STYLES[kind].color }}>{THREAD_TYPE_LABELS[kind]}</span>
      <span className="rail-title">{kind === 'SYSTEM' ? 'TradeMagic' : username}</span>
    </header>
    <p className="rail-unlinked-title">Not linked to a trade</p>
    <p className="rail-unlinked-copy">
      {kind === 'SYSTEM'
        ? 'Account notices and announcements from TradeMagic land here. Messages about a specific trade, offer or case have their own threads.'
        : `General chat with ${username}. Messages sent from a trade page open a thread for that trade instead.`}
    </p>
  </>
);

interface Props {
  conversationId: string;
  viewerUsername?: string;
  /**
   * Opens the Summon Admin wizard (G6). Until it lands the rail falls back to
   * the one-click summon the Contact modal used, with a confirm.
   */
  onSummon?: (trade: TradeThreadContext) => void;
  /** Mobile sheet footer; omitted on desktop so actions stay inline. */
  actionsPortalTarget?: HTMLElement | null;
}

/**
 * The context rail (Decision 12): what the open thread is about, per type.
 * Reads the same thread query as the pane, so RTK serves it from cache.
 * Summon Admin opens the wizard with source INBOX (Decision 24).
 */
export const ContextRail: React.FC<Props> = ({ conversationId, viewerUsername, onSummon, actionsPortalTarget }) => {
  // currentData, not data: data keeps the previous thread's result while a
  // new id loads or fails, which left the last rail standing beside a 404 pane.
  const { currentData: thread, isFetching, isError } = useGetConversationThreadQuery(conversationId);
  const [wizardTrade, setWizardTrade] = useState<TradeThreadContext | null>(null);

  if (isError) {
    return <div className="context-rail" />;
  }
  if (isFetching && !thread) {
    return <div className="context-rail" aria-busy="true" />;
  }
  if (!thread) {
    return <div className="context-rail" />;
  }

  const context = thread.context;
  const subjectType = threadSubjectType(thread);
  const openWizard = onSummon ?? ((trade: TradeThreadContext) => setWizardTrade(trade));

  return (
    <div className="context-rail">
      {wizardTrade && (
        <SummonAdminWizard
          isOpen
          onClose={() => setWizardTrade(null)}
          trade={summaryFromTradeContext(wizardTrade, thread.otherUser.username)}
          source="INBOX"
        />
      )}
      {isTradeContext(context) ? (
        <TradeRail trade={context} viewerUsername={viewerUsername} partnerUsername={thread.otherUser.username} onSummon={openWizard} actionsPortalTarget={actionsPortalTarget} />
      ) : isCaseContext(context) ? (
        <CaseRail caseContext={context} viewerUsername={viewerUsername} partnerUsername={thread.otherUser.username} actionsPortalTarget={actionsPortalTarget} />
      ) : isOfferContext(context) ? (
        <OfferRail offer={context} actionsPortalTarget={actionsPortalTarget} />
      ) : (
        <UnlinkedRail kind={subjectType === 'SYSTEM' ? 'SYSTEM' : 'DIRECT'} username={thread.otherUser.username} />
      )}
    </div>
  );
};

export default ContextRail;
